import React, { useEffect, useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PrintIcon from "@mui/icons-material/Print";
import ShareIcon from "@mui/icons-material/Share";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import logo from "../../images/logo.png";
import { calculateMedicationDose, buildPrescriptionText, formatNumber } from "./calculation";
import { medications, MEDICATION_CATEGORIES } from "./medications";
import { MedicationGlobalStyle, Page } from "./styles";

const PROFILE_KEY = "medicationProfessionalProfile";

const emptyProfile = {
  name: "",
  cro: "",
  state: "PE",
  clinic: "Consultório Dra. Aline Oliveira",
  address: "",
  phone: "",
};

const safetyOptions = [
  ["penicillinAllergy", "Alergia a penicilinas/betalactâmicos"],
  ["nsaidAllergy", "Alergia a anti-inflamatórios"],
  ["kidneyDisease", "Doença renal"],
  ["liverDisease", "Doença hepática"],
  ["pregnancy", "Gestação ou amamentação"],
  ["otherCondition", "Outra condição relevante"],
];

const escapeHtml = (value = "") =>
  String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

function MedicacaoPage() {
  const [patient, setPatient] = useState({
    name: "",
    ageValue: "",
    ageUnit: "years",
    weightKg: "",
    observations: "",
  });
  const [safety, setSafety] = useState({});
  const [profile, setProfile] = useState(emptyProfile);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("Todos");
  const [selectedId, setSelectedId] = useState("amoxicilina");
  const [regimenId, setRegimenId] = useState("odontogenic-q8h");
  const [inputs, setInputs] = useState({
    concentrationMgPerMl: "50",
    doseMgKg: "",
    dosesPerDay: "",
    durationDays: "",
    maxSingleMg: "",
    maxDailyMg: "",
    manualCalculation: "mgKgDose",
    manualFixedDoseMg: "",
  });
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    document.title = "Medicação - Utilitários Dra. Aline Oliveira";
    try {
      const stored = JSON.parse(localStorage.getItem(PROFILE_KEY));
      if (stored) setProfile({ ...emptyProfile, ...stored });
    } catch {
      localStorage.removeItem(PROFILE_KEY);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  }, [profile]);

  const selectedMedication = useMemo(
    () => medications.find((medication) => medication.id === selectedId) || medications[0],
    [selectedId]
  );

  const selectedRegimen = useMemo(
    () =>
      selectedMedication.regimens.find((regimen) => regimen.id === regimenId) ||
      selectedMedication.regimens[0],
    [selectedMedication, regimenId]
  );

  useEffect(() => {
    setRegimenId(selectedMedication.regimens[0].id);
    setInputs((current) => ({
      ...current,
      concentrationMgPerMl: selectedMedication.concentrationMgPerMl?.toString() || "",
      doseMgKg: "",
      dosesPerDay: "",
      durationDays: "",
      maxSingleMg: "",
      maxDailyMg: "",
      manualFixedDoseMg: "",
    }));
  }, [selectedMedication]);

  const filteredMedications = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("pt-BR");
    return medications.filter((medication) => {
      const categoryMatches = category === "Todos" || medication.category === category;
      const searchMatches =
        !normalized ||
        `${medication.name} ${medication.presentation} ${medication.category}`
          .toLocaleLowerCase("pt-BR")
          .includes(normalized);
      return categoryMatches && searchMatches;
    });
  }, [category, query]);

  const result = useMemo(
    () =>
      calculateMedicationDose({
        medication: selectedMedication,
        regimen: selectedRegimen,
        weightKg: patient.weightKg,
        ageValue: patient.ageValue,
        ageUnit: patient.ageUnit,
        concentrationMgPerMl: inputs.concentrationMgPerMl,
        doseMgKg: inputs.doseMgKg,
        dosesPerDay: inputs.dosesPerDay,
        durationDays: inputs.durationDays,
        maxSingleMg: inputs.maxSingleMg,
        maxDailyMg: inputs.maxDailyMg,
        manualCalculation: inputs.manualCalculation,
        manualFixedDoseMg: inputs.manualFixedDoseMg,
      }),
    [inputs, patient, selectedMedication, selectedRegimen]
  );

  const prescriptionText = useMemo(
    () =>
      buildPrescriptionText({
        medication: selectedMedication,
        regimen: selectedRegimen,
        result,
        patientName: patient.name,
        professional: profile,
      }),
    [patient.name, profile, result, selectedMedication, selectedRegimen]
  );

  const clinicalAlerts = useMemo(() => {
    const alerts = [...(selectedMedication.warnings || [])];
    if (safety.penicillinAllergy && selectedMedication.category === "Antibióticos") {
      alerts.unshift("Paciente informou alergia a penicilinas/betalactâmicos. Revisar o medicamento selecionado.");
    }
    if (safety.nsaidAllergy && selectedMedication.category === "Anti-inflamatórios") {
      alerts.unshift("Paciente informou alergia a anti-inflamatórios. Não prosseguir sem reavaliação.");
    }
    if (safety.kidneyDisease) alerts.push("Doença renal informada: revisar dose, intervalo e indicação.");
    if (safety.liverDisease) alerts.push("Doença hepática informada: revisar dose, limites e indicação.");
    if (safety.pregnancy) alerts.push("Gestação/amamentação informada: confirmar segurança e indicação.");
    return alerts;
  }, [safety, selectedMedication]);

  const updatePatient = (field) => (event) =>
    setPatient((current) => ({ ...current, [field]: event.target.value }));
  const updateProfile = (field) => (event) =>
    setProfile((current) => ({ ...current, [field]: event.target.value }));
  const updateInput = (field) => (event) =>
    setInputs((current) => ({ ...current, [field]: event.target.value }));

  const chooseMedication = (id) => {
    setSelectedId(id);
    setFeedback("");
  };

  const isApproved = selectedMedication.review.status === "approved";
  const canCreateDocument = result.valid && patient.name.trim() && profile.name.trim();
  const documentLabel =
    isApproved && selectedMedication.documentType !== "clinical-summary"
      ? "Prescrição"
      : "Resumo clínico de apoio";

  const copyPrescription = async () => {
    if (!prescriptionText) return;
    await navigator.clipboard.writeText(`${documentLabel}\n\n${prescriptionText}`);
    setFeedback("Texto copiado.");
  };

  const sharePrescription = async () => {
    if (!prescriptionText) return;
    const text = `${documentLabel}\n\n${prescriptionText}`;
    if (navigator.share) {
      await navigator.share({ title: documentLabel, text });
      setFeedback("Resumo compartilhado.");
      return;
    }
    await navigator.clipboard.writeText(text);
    setFeedback("Compartilhamento indisponível. O texto foi copiado.");
  };

  const printDocument = () => {
    if (!canCreateDocument) return;
    const printWindow = window.open("", "_blank", "noopener,noreferrer");
    if (!printWindow) {
      setFeedback("O navegador bloqueou a janela de impressão.");
      return;
    }

    const copies = selectedMedication.documentType === "antibiotic" && isApproved ? 2 : 1;
    const copyMarkup = Array.from({ length: copies })
      .map(
        (_, index) => `
          <section class="document ${index ? "second-copy" : ""}">
            <header>
              <h1>${escapeHtml(profile.clinic || documentLabel)}</h1>
              <p>${escapeHtml(profile.name)} · CRO ${escapeHtml(profile.cro)} / ${escapeHtml(profile.state)}</p>
              <p>${escapeHtml(profile.address)} ${profile.phone ? `· ${escapeHtml(profile.phone)}` : ""}</p>
            </header>
            <h2>${escapeHtml(documentLabel)}${copies === 2 ? ` · ${index === 0 ? "1ª via" : "2ª via"}` : ""}</h2>
            <pre>${escapeHtml(prescriptionText)}</pre>
            ${
              patient.observations
                ? `<p><strong>Observações:</strong> ${escapeHtml(patient.observations)}</p>`
                : ""
            }
            <div class="warning">Regime pendente de revisão clínica. A decisão terapêutica permanece sob responsabilidade do profissional.</div>
            <footer>
              <span>Data: ${new Intl.DateTimeFormat("pt-BR").format(new Date())}</span>
              <span class="signature">Assinatura e carimbo</span>
            </footer>
          </section>`
      )
      .join("");

    printWindow.document.write(`
      <!doctype html>
      <html lang="pt-BR">
        <head>
          <meta charset="utf-8">
          <title>${escapeHtml(documentLabel)}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #24191a; margin: 0; padding: 24px; }
            .document { min-height: 42vh; border: 1px solid #cabbbc; padding: 28px; }
            .second-copy { margin-top: 28px; page-break-before: always; }
            header { border-bottom: 3px solid #310204; padding-bottom: 14px; margin-bottom: 24px; }
            h1, h2 { color: #310204; margin: 0 0 8px; }
            p { margin: 5px 0; }
            pre { white-space: pre-wrap; font: 16px/1.6 Arial, sans-serif; border: 1px solid #ddd; padding: 18px; }
            .warning { background: #fff4d4; border: 1px solid #d7aa36; padding: 12px; margin-top: 20px; }
            footer { display: flex; justify-content: space-between; gap: 30px; margin-top: 60px; }
            .signature { min-width: 230px; border-top: 1px solid #222; padding-top: 6px; text-align: center; }
            @media print { body { padding: 0; } .document { border: 0; } }
          </style>
        </head>
        <body>${copyMarkup}<script>window.onload = () => window.print();</script></body>
      </html>
    `);
    printWindow.document.close();
  };

  const showManualFields = selectedRegimen.calculation === "manual";
  const activeDose = inputs.doseMgKg || selectedRegimen.doseMgKg || "";
  const activeDosesPerDay = inputs.dosesPerDay || selectedRegimen.dosesPerDay || "";
  const activeDuration = inputs.durationDays || selectedRegimen.durationDays || "";

  return (
    <>
      <MedicationGlobalStyle />
      <Page>
        <section className="hero">
          <div className="brand">
            <img src={logo} alt="Consultório Dra. Aline Oliveira" />
            <div>
              <h1>Calculadora de medicações</h1>
              <p>Calcule doses, confira alertas e prepare um resumo clínico para revisão profissional.</p>
            </div>
          </div>
          <div className="review-banner">
            Catálogo em revisão clínica. Nenhum regime deve ser usado sem conferência profissional.
          </div>
        </section>

        <section className="workspace">
          <div className="column">
            <section className="card">
              <div className="card-header">
                <h2><span className="step">1</span>Dados do paciente</h2>
              </div>
              <div className="grid">
                <div className="field full">
                  <label htmlFor="patient-name">Nome do paciente</label>
                  <input id="patient-name" value={patient.name} onChange={updatePatient("name")} placeholder="Obrigatório somente para documentos" />
                </div>
                <div className="field">
                  <label htmlFor="age">Idade</label>
                  <input id="age" type="number" min="0" value={patient.ageValue} onChange={updatePatient("ageValue")} />
                </div>
                <div className="field">
                  <label htmlFor="age-unit">Unidade</label>
                  <select id="age-unit" value={patient.ageUnit} onChange={updatePatient("ageUnit")}>
                    <option value="days">Dias</option>
                    <option value="months">Meses</option>
                    <option value="years">Anos</option>
                  </select>
                </div>
                <div className="field full">
                  <label htmlFor="weight">Peso (kg)</label>
                  <input id="weight" type="number" min="0" step="0.1" value={patient.weightKg} onChange={updatePatient("weightKg")} placeholder="Ex.: 16" />
                </div>
                <div className="field full">
                  <span className="field-label">Condições clínicas informadas</span>
                  <div className="check-grid">
                    {safetyOptions.map(([key, label]) => (
                      <label className="check" key={key}>
                        <input
                          type="checkbox"
                          checked={Boolean(safety[key])}
                          onChange={(event) => setSafety((current) => ({ ...current, [key]: event.target.checked }))}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="field full">
                  <label htmlFor="observations">Observações clínicas</label>
                  <textarea id="observations" value={patient.observations} onChange={updatePatient("observations")} placeholder="Alergias, comorbidades, medicamentos em uso..." />
                </div>
              </div>
            </section>

            <section className="card">
              <div className="card-header">
                <h2>Dados do profissional</h2>
                <span className="subtle">salvos somente neste dispositivo</span>
              </div>
              <div className="grid">
                <div className="field full">
                  <label htmlFor="profile-name">Nome</label>
                  <input id="profile-name" value={profile.name} onChange={updateProfile("name")} placeholder="Dra. Aline Oliveira" />
                </div>
                <div className="field">
                  <label htmlFor="profile-cro">CRO</label>
                  <input id="profile-cro" value={profile.cro} onChange={updateProfile("cro")} />
                </div>
                <div className="field">
                  <label htmlFor="profile-state">UF</label>
                  <input id="profile-state" maxLength="2" value={profile.state} onChange={updateProfile("state")} />
                </div>
                <div className="field full">
                  <label htmlFor="profile-clinic">Consultório</label>
                  <input id="profile-clinic" value={profile.clinic} onChange={updateProfile("clinic")} />
                </div>
                <div className="field full">
                  <label htmlFor="profile-address">Endereço profissional</label>
                  <input id="profile-address" value={profile.address} onChange={updateProfile("address")} />
                </div>
                <div className="field full">
                  <label htmlFor="profile-phone">Telefone</label>
                  <input id="profile-phone" value={profile.phone} onChange={updateProfile("phone")} />
                </div>
              </div>
            </section>

            <section className="card">
              <div className="card-header">
                <h2><span className="step">2</span>Seleção do medicamento</h2>
              </div>
              <div className="search">
                <SearchIcon className="search-icon" fontSize="small" />
                <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar medicamento ou apresentação..." />
              </div>
              <div className="chips">
                {MEDICATION_CATEGORIES.map((item) => (
                  <button key={item} className={`chip ${category === item ? "active" : ""}`} onClick={() => setCategory(item)}>
                    {item}
                  </button>
                ))}
              </div>
              <div className="medication-list">
                {filteredMedications.map((medication) => (
                  <button
                    key={medication.id}
                    className={`medication-item ${selectedId === medication.id ? "selected" : ""}`}
                    onClick={() => chooseMedication(medication.id)}
                  >
                    <strong>{medication.name}</strong>
                    <span>{medication.presentation}</span>
                    <span className="category">{medication.category}</span>
                  </button>
                ))}
              </div>
            </section>
          </div>

          <div className="column">
            <section className="card">
              <div className="card-header">
                <h2><span className="step">3</span>Regime e apresentação</h2>
                <span className={`status ${isApproved ? "ready" : "pending"}`}>
                  {isApproved ? "Aprovado" : "Pendente de revisão"}
                </span>
              </div>
              <div className="selected-medication">
                <strong>{selectedMedication.name}</strong>
                <span>{selectedMedication.presentation}</span>
                <br />
                <a href={selectedMedication.leafletUrl} target="_blank" rel="noreferrer">
                  Consultar bula <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: "middle" }} />
                </a>
              </div>
              <div className="grid">
                <div className="field full">
                  <label htmlFor="regimen">Indicação/regime</label>
                  <select id="regimen" value={regimenId} onChange={(event) => setRegimenId(event.target.value)}>
                    {selectedMedication.regimens.map((regimen) => (
                      <option key={regimen.id} value={regimen.id}>{regimen.label}</option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="concentration">Concentração (mg/mL)</label>
                  <input id="concentration" type="number" min="0" step="0.01" value={inputs.concentrationMgPerMl} onChange={updateInput("concentrationMgPerMl")} placeholder="Informar quando aplicável" />
                </div>
                <div className="field">
                  <label htmlFor="dose">Dose (mg/kg)</label>
                  <input id="dose" type="number" min="0" step="0.1" value={activeDose} onChange={updateInput("doseMgKg")} disabled={showManualFields && inputs.manualCalculation === "fixedMg"} />
                </div>
                <div className="field">
                  <label htmlFor="doses-day">Doses por dia</label>
                  <input id="doses-day" type="number" min="1" step="1" value={activeDosesPerDay} onChange={updateInput("dosesPerDay")} />
                </div>
                <div className="field">
                  <label htmlFor="duration">Duração (dias)</label>
                  <input id="duration" type="number" min="1" step="1" value={activeDuration} onChange={updateInput("durationDays")} />
                </div>
                {showManualFields && (
                  <>
                    <div className="field">
                      <label htmlFor="manual-calculation">Tipo de cálculo manual</label>
                      <select id="manual-calculation" value={inputs.manualCalculation} onChange={updateInput("manualCalculation")}>
                        <option value="mgKgDose">mg/kg por dose</option>
                        <option value="mgKgDay">mg/kg por dia</option>
                        <option value="fixedMg">Dose fixa em mg</option>
                      </select>
                    </div>
                    {inputs.manualCalculation === "fixedMg" && (
                      <div className="field">
                        <label htmlFor="fixed-dose">Dose fixa (mg)</label>
                        <input id="fixed-dose" type="number" min="0" step="0.01" value={inputs.manualFixedDoseMg} onChange={updateInput("manualFixedDoseMg")} />
                      </div>
                    )}
                  </>
                )}
                <div className="field">
                  <label htmlFor="max-single">Máximo por tomada (mg)</label>
                  <input id="max-single" type="number" min="0" value={inputs.maxSingleMg || selectedRegimen.maxSingleMg || ""} onChange={updateInput("maxSingleMg")} />
                </div>
                <div className="field">
                  <label htmlFor="max-daily">Máximo diário (mg)</label>
                  <input id="max-daily" type="number" min="0" value={inputs.maxDailyMg || selectedRegimen.maxDailyMg || ""} onChange={updateInput("maxDailyMg")} />
                </div>
              </div>
            </section>

            <section className="card dose-card">
              <div className="card-header">
                <h2>Dose calculada por administração</h2>
                <span className="status pending">{selectedMedication.category}</span>
              </div>
              {result.valid ? (
                <>
                  <div className="dose-main">
                    <strong>{formatNumber(result.finalSingleMg)} mg</strong>
                    {result.volumeMl && <span>≈ {formatNumber(result.volumeMl)} mL</span>}
                    {result.drops && <span>≈ {result.drops} gotas</span>}
                  </div>
                  <p className="subtle">
                    Dose diária: {formatNumber(result.finalDailyMg)} mg
                    {result.limited ? " · limite máximo aplicado" : ""}
                  </p>
                </>
              ) : (
                <div className="empty">Preencha idade, peso e regime para calcular.</div>
              )}
            </section>

            <section className="card">
              <div className="card-header">
                <h2>Prescrição sugerida</h2>
                <span className={`status ${isApproved ? "ready" : "pending"}`}>{documentLabel}</span>
              </div>
              {result.valid ? (
                <>
                  <div className="metric-grid">
                    <div className="metric"><span>Frequência</span><strong>{result.intervalHours ? `${formatNumber(result.intervalHours, 1)}/${formatNumber(result.intervalHours, 1)}h` : `${result.dosesPerDay}x/dia`}</strong></div>
                    <div className="metric"><span>Doses/dia</span><strong>{result.dosesPerDay}</strong></div>
                    <div className="metric"><span>Duração</span><strong>{result.durationDays ? `${result.durationDays} dias` : "informar"}</strong></div>
                  </div>
                  <div className="prescription">{prescriptionText || "Resultado disponível. Informe o nome do paciente e o perfil profissional para completar o documento."}</div>
                  <div className="button-row">
                    <button className="button" onClick={copyPrescription} disabled={!prescriptionText}><ContentCopyIcon fontSize="small" />Copiar</button>
                    <button className="button secondary" onClick={printDocument} disabled={!canCreateDocument}><PrintIcon fontSize="small" />Imprimir / salvar PDF</button>
                    <button className="button secondary" onClick={sharePrescription} disabled={!prescriptionText}><ShareIcon fontSize="small" />Compartilhar</button>
                  </div>
                  {feedback && <p className="success-message">{feedback}</p>}
                  {!canCreateDocument && <p className="profile-note">Informe o nome do paciente e do profissional para gerar documento.</p>}
                </>
              ) : (
                <div className="empty">O resumo será exibido quando o cálculo estiver válido.</div>
              )}
            </section>

            <section className="card">
              <div className="card-header"><h2>Alertas e informações adicionais</h2></div>
              <div className="alerts">
                {result.errors?.map((error) => <div className="alert error" key={error}><WarningAmberIcon fontSize="small" />{error}</div>)}
                {result.warnings?.map((warning) => <div className="alert warning" key={warning}><WarningAmberIcon fontSize="small" />{warning}</div>)}
                {clinicalAlerts.map((alert) => <div className="alert warning" key={alert}><WarningAmberIcon fontSize="small" />{alert}</div>)}
                {selectedRegimen.notes && <div className="alert info"><InfoOutlinedIcon fontSize="small" />{selectedRegimen.notes}</div>}
                <div className="alert info"><InfoOutlinedIcon fontSize="small" />Ferramenta de apoio clínico. A decisão terapêutica permanece sob responsabilidade do profissional.</div>
              </div>
            </section>
          </div>
        </section>
      </Page>
    </>
  );
}

export default MedicacaoPage;
